import * as React from "react";
import { ScrollView } from "react-native";
import { Div, Label, Touchable } from "@grassrootly/rn-core";
import { GameInfoProps, GameTabsProps } from "./GamePreview.types";
import { getGameMVP, isFinished } from "./lib";
import PreGameAttendees from "./game-attendees/PreGameAttendees";
import GameDetails from "./game-details/GameDetails";
import GamePlayers from "./game-attendees/GamePlayers";
import { DEFAULT_TEAMS, GameStatues } from "@grassrootly/api";
import GameMVP from "./game-mvp/GameMVP";
import { GameType } from "../../screens/new-game/NewGame.types";
import GameLineups from "./game-lineups/GameLineups";

const TopBar = ({ route, onPress, isActive }: GameTabsProps) => (
  <Touchable
    classNames={[
      "flex1",
      "alignItemsCenter",
      "justifyContentCenter",
      "pv16",
      "bwBottom-{1}",
      isActive ? "borderPrimary" : "borderSecondary",
      "mb-{8}",
    ]}
    key={route.key}
    onPress={onPress}
  >
    <Label>{route.title}</Label>
  </Touchable>
);

export default function GameInfo({ game, navigation }: GameInfoProps) {
  const [index, setIndex] = React.useState(0);

  const { routes } = React.useMemo(() => {
    let r = { routes: [] as any[] };
    
    // SCHEDULED status logic
    if (game.status === GameStatues.SCHEDULED) {
      // For ORGANIZATION_MATCH: Show only Details (no Lineups until FINISHED/FINALIZED)
      if (game.game_type === GameType.ORGANIZATION_MATCH) {
        r = {
          routes: [
            {
              key: "details",
              title: "DETAILS",
              renderComponent: () => <GameDetails key={0} game={game} />,
            },
          ],
        };
      } else {
        // For TRAINING and COMPETITIVE: Show Attending + Details
        r = {
          routes: [
            {
              key: "attendees",
              title: "ATTENDING",
              renderComponent: () => <PreGameAttendees key={0} attendees={game.attendees} />,
            },
            {
              key: "details",
              title: "DETAILS",
              renderComponent: () => <GameDetails key={1} game={game} />,
            },
          ],
        };
      }
    }
    // CONFIRMED, FINISHED, FINALIZED status logic
    else if (
      game.status === GameStatues.CONFIRMED ||
      game.status === GameStatues.FINISHED ||
      game.status === GameStatues.FINALIZED
    ) {
      // For ORGANIZATION_MATCH: Show Lineups only for FINISHED/FINALIZED
      if (game.game_type === GameType.ORGANIZATION_MATCH) {
        if (game.status === GameStatues.FINISHED || game.status === GameStatues.FINALIZED) {
          r = {
            routes: [
              {
                key: "lineups",
                title: "LINEUPS",
                renderComponent: () => (
                  <GameLineups key={0} game={game} showPoints={game.status === GameStatues.FINALIZED} navigation={navigation} />
                ),
              },
              {
                key: "details",
                title: "DETAILS",
                renderComponent: () => <GameDetails key={1} game={game} />,
              },
            ],
          };
        } else {
          // CONFIRMED status: Show only Details
          r = {
            routes: [
              {
                key: "details",
                title: "DETAILS",
                renderComponent: () => <GameDetails key={0} game={game} />,
              },
            ],
          };
        }
      } else {
        // For TRAINING and COMPETITIVE: Show Lineups + Details
        r = {
          routes: [
            {
              key: "lineups",
              title: "LINEUPS",
              renderComponent: () => (
                <GameLineups key={0} game={game} showPoints={game.status === GameStatues.FINALIZED} navigation={navigation} />
              ),
            },
            {
              key: "details",
              title: "DETAILS",
              renderComponent: () => <GameDetails key={1} game={game} />,
            },
          ],
        };
      }
    }
    // Fallback for any other status
    else {
      r = {
        routes: [
          {
            key: "details",
            title: "DETAILS",
            renderComponent: () => <GameDetails key={0} game={game} />,
          },
        ],
      };
    }
    
    return r;
  }, [game, game.attendees]);

  return (
    <Div classNames={["mt-{16}", "flex1"]}>
      {game.status === GameStatues.FINALIZED && game.attendees.length ? (
        <GameMVP mvp={getGameMVP(game.attendees)} navigation={navigation} />
      ) : null}
      <Div classNames={["flexRow", "bgNeutralDarkerDark"]}>
        {routes.map((route, i) => (
          <TopBar key={route.key} route={route} onPress={() => setIndex(i)} isActive={i === index} />
        ))}
      </Div>
      <ScrollView style={{ flex: 1 }}>
        {routes.map((route, i) => (i === index ? route.renderComponent() : null))}
      </ScrollView>
    </Div>
  );
}
